import { IViewerApi, RendererTypes } from "@bronte/viewer-api/src/main";

import { getPackageGlossary } from "./searchService";
import { CsgGlossaryItem } from "./searchTypes";
import { getSpotlightApi } from "./spotlightApi";

export interface SearchNavigateTarget {
  pageUrn: string;
  dataId?: string;
  /** Search result product id — used by SearchDrawer to mark the active hit. */
  productId?: string;
  /** Element to highlight in when it differs from the scroll target (e.g. glossary definition). */
  highlightDataId?: string;
}

const SEARCH_HIGHLIGHT_COLOR = "rgb(213, 213, 213)";
const HIGHLIGHT_POLL_INTERVAL_MS = 200;
const HIGHLIGHT_MAX_RETRIES = 30;

function isExpressRenderer(): boolean {
  return process.env.VITE_BV_RENDERER === RendererTypes.EXPRESS;
}

function applySearchHighlightWhenReady(
  viewerApi: IViewerApi,
  dataId: string,
  searchText: string,
): void {
  const spotlight = getSpotlightApi(viewerApi);
  if (!spotlight) {
    return;
  }

  let retries = 0;

  const tryApply = (): void => {
    retries += 1;
    const element = document.querySelector(`[data-id="${dataId}"]`);
    if (element) {
      spotlight.apply({
        selector: dataId,
        useTextSearch: true,
        text: searchText,
        mark: [
          {
            startOffset: 0,
            length: searchText.length,
            backgroundColor: SEARCH_HIGHLIGHT_COLOR,
          },
        ],
      });
      return;
    }

    if (retries < HIGHLIGHT_MAX_RETRIES) {
      setTimeout(tryApply, HIGHLIGHT_POLL_INTERVAL_MS);
    }
  };

  setTimeout(tryApply, HIGHLIGHT_POLL_INTERVAL_MS);
}

export function navigateToSearchResult(
  viewerApi: IViewerApi | undefined,
  target: SearchNavigateTarget,
  searchText?: string,
): void {
  if (!viewerApi || !target.pageUrn) {
    return;
  }

  const trimmedText = searchText?.trim();

  if (target.dataId) {
    const subTarget = isExpressRenderer()
      ? { dataId: target.dataId }
      : { dataId: target.dataId, ...(trimmedText ? { text: trimmedText } : {}) };

    viewerApi.route(target.pageUrn, subTarget);

    if (isExpressRenderer() && trimmedText) {
      applySearchHighlightWhenReady(
        viewerApi,
        target.highlightDataId ?? target.dataId,
        trimmedText,
      );
    }
    return;
  }

  viewerApi.route(target.pageUrn);
}

/** Case-insensitive match of `query` in `text`, returned as written in `text`. */
function findMatch(text: string, query: string): string | null {
  const needle = query.trim().toLowerCase();
  if (!text || !needle) {
    return null;
  }
  const index = text.toLowerCase().indexOf(needle);
  return index === -1 ? null : text.slice(index, index + needle.length);
}

/**
 * Routes to the Glossary screen (`glossary.json` ancestry[0]) and scrolls to the term.
 * The term block's `data-id` is the package glossary `id`; match on CSG `itemid` first,
 * then on term text, falling back to the CSG `itemid`.
 * The searched text is highlighted in the term, or in the definition block
 * (`data-id="<id>-definition"`) when only the definition matched.
 */
export async function navigateToGlossaryTerm(
  viewerApi: IViewerApi | undefined,
  packagePath: string,
  item: CsgGlossaryItem,
  query = "",
): Promise<boolean> {
  if (!viewerApi) {
    return false;
  }

  const glossary = await getPackageGlossary(packagePath);
  const glossaryScreenId = glossary?.ancestry[0];
  if (!glossaryScreenId) {
    return false;
  }

  const termText = item.term?.text?.trim() ?? "";
  const normalizedTerm = termText.toLowerCase();
  const entry =
    glossary.data.find((candidate) => candidate.id === item.itemid) ??
    glossary.data.find((candidate) => candidate.key?.trim().toLowerCase() === normalizedTerm);

  const termId = entry?.id ?? item.itemid;
  const termMatch = findMatch(termText, query);
  const definitionMatch = termMatch ? null : findMatch(item.definition?.text ?? "", query);

  navigateToSearchResult(
    viewerApi,
    {
      pageUrn: glossaryScreenId,
      dataId: termId,
      ...(definitionMatch ? { highlightDataId: `${termId}-definition` } : {}),
    },
    termMatch ?? definitionMatch ?? termText,
  );
  return true;
}
